import { useEffect, useState } from "react";
import {
  Box,
  useToast,
} from "@chakra-ui/react";
import ConciliationFilters from "./ConciliationFilters";
import ConciliationContentView from "./ConciliationContentView";
import ConciliationConfigModal from "./ConciliationConfigModal";
import {
  ConsigConciliationFilters,
  initialConsigConciliationFilters,
} from "@/models/consig-conciliation-filters.model";
import {
  CondicaoSeguroConciliacao,
  ConfiguracaoConciliacao,
  CreateConfiguracaoConciliacaoPayload,
  FONTES_CONCILIACAO,
  FonteConciliacao,
  RegraConciliacao,
} from "@/models/conciliation-config.model";
import {
  createConfiguracaoConciliacao,
  deleteConfiguracaoConciliacao,
  getConfiguracaoConciliacaoById,
  listConfiguracoesConciliacao,
  updateConfiguracaoConciliacao,
} from "@/services/conciliationConfig.service";
import {
  executarConciliacaoEmMassa,
  getPropostasFinanceiras,
  getPropostasFinanceirasListView,
} from "@/services/consignado/conciliation.service";
import { EtapaConciliacaoStatusItem, ProposalSidebarItem } from "./ConciliationSidebar";
import { ConciliacaoStatus, ViewModeType } from "@/models/consignado/propostaFinanceira.model";
import ConsignadoListView from "./ListView";

interface CommissionsTabProps {
  backgroundStyle: Record<string, unknown>;
}
const MAX_MASS_RECONCILIATION_ITEMS = 10;

const isValidStatusConciliacao = (value: unknown): value is ConciliacaoStatus => {
  return value === ConciliacaoStatus.PENDENTE
    || value === ConciliacaoStatus.CONCLUIDO
    || value === ConciliacaoStatus.DIVERGENTE
    || value === ConciliacaoStatus.NAO_SE_APLICA;
};

const normalizeEtapasConciliacao = (value: unknown): EtapaConciliacaoStatusItem[] => {
  if (!Array.isArray(value))
    return [];

  const etapas: EtapaConciliacaoStatusItem[] = [];

  value.forEach((item) => {
    if (!item || typeof item !== "object")
      return;

    const etapaRecord = item as Record<string, unknown>;
    const etapa = String(etapaRecord.etapa ?? "");

    if (!etapa)
      return;

    const status = isValidStatusConciliacao(etapaRecord.status)
      ? etapaRecord.status
      : ConciliacaoStatus.PENDENTE;

    etapas.push({
      etapa,
      status,
      observacao: etapaRecord.observacao ? String(etapaRecord.observacao) : undefined,
      createdAt: etapaRecord.createdAt ? String(etapaRecord.createdAt) : undefined,
      updatedAt: etapaRecord.updatedAt ? String(etapaRecord.updatedAt) : undefined,
    });
  });

  return etapas;
};

const normalizeProposalItem = (item: Record<string, unknown>): ProposalSidebarItem => {
  const id = String(item.id ?? item.proposalId ?? "");

  const statusConciliacao = isValidStatusConciliacao(item.statusConciliacao)
    ? item.statusConciliacao
    : ConciliacaoStatus.PENDENTE;

  return {
    id,
    shortHash: String(item.shorthash ?? item.shortHash ?? ""),
    valorLiberado: Number(item.valor_liberado ?? item.valorLiberado ?? item.releasedAmount ?? item.amount ?? 0),
    valorParcela: Number(item.valor_parcela ?? item.valorParcela ?? item.installmentValue ?? 0),
    quantidadeParcela: Number(item.quantidade_parcela ?? item.quantidadeParcela ?? item.numberOfInstallments ?? 0),
    valorSeguro: Number(item.valor_seguro ?? item.valorSeguro ?? item.insuranceValue ?? 0),
    valorIof: Number(item.valor_iof ?? item.valorIof ?? item.iofAmount ?? item.iof ?? 0),
    valorDesconto: Number(item.valor_desconto ?? item.valorDesconto ?? item.discountAmount ?? 0),
    processorName: String(item.processor_name ?? item.processorName ?? ""),
    insuranceCompanyName: String(
      item.insurance_company_name ?? item.insuranceCompanyName ?? ""
    ),
    dataCriacaoProposta: String(item.data_proposta ?? item.dataProposta ?? item.dataCriacaoProposta ?? item.createdAt ?? ""),
    numeroCcb: String(item.numero_ccb ?? item.numeroCcb ?? item.ccb_number ?? ""),
    statusConciliacao,
    etapasConciliacao: normalizeEtapasConciliacao(item.etapasConciliacao),
    // relatorios: Array.isArray(item.relatorios)
    //   ? item.relatorios.map(r => ({
    //       tipo: String(r.tipo ?? ""),
    //       status: isValidStatusConciliacao(r.status) ? r.status : ConciliacaoStatus.PENDENTE,
    //       propostaFinanceiraId: String(r.propostaFinanceiraId ?? ""),
    //     }))
    //   : [],
  };
};

const createEmptyRegra = (): RegraConciliacao => ({
  descricao: "",
  obrigatoria: false,
  tolerancia: 0,
  operador: "IGUAL",
  origem: {
    fonte: "",
    campo: "",
    rotulo: "",
  },
  destino: {
    fonte: "",
    campo: "",
    rotulo: "",
  },
});

const createInitialConfiguracaoForm = (): CreateConfiguracaoConciliacaoPayload => ({
  nome: "",
  ordem: 0,
  condicaoSeguro: "QUALQUER",
  configuracao: {
    versao: 1,
    regras: [createEmptyRegra()],
  },
});

const isFonteConciliacao = (value: string): value is FonteConciliacao => {
  return FONTES_CONCILIACAO.includes(value as FonteConciliacao);
};

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === "object" && value !== null;
};

const parseConfiguracaoConteudo = (value: unknown): Record<string, unknown> => {
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value) as unknown;
      return isRecord(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }

  if (isRecord(value))
    return value;

  return {};
};

const normalizeRegra = (regra: unknown): RegraConciliacao => {
  const regraRecord = isRecord(regra) ? regra : {};

  const origemRecord = isRecord(regraRecord.origem) ? regraRecord.origem : {};
  const destinoRecord = isRecord(regraRecord.destino) ? regraRecord.destino : {};

  const origemFonteValue = String(origemRecord.fonte ?? "");
  const destinoFonteValue = String(destinoRecord.fonte ?? "");

  const origemFonte: FonteConciliacao | "" =
    origemFonteValue && isFonteConciliacao(origemFonteValue)
      ? origemFonteValue
      : "";

  const destinoFonte: FonteConciliacao | "" =
    destinoFonteValue && isFonteConciliacao(destinoFonteValue)
      ? destinoFonteValue
      : "";

  return {
    descricao: String(regraRecord.descricao ?? ""),
    obrigatoria: Boolean(regraRecord.obrigatoria),
    tolerancia: Number(regraRecord.tolerancia ?? 0),
    operador: String(regraRecord.operador ?? "IGUAL") as RegraConciliacao["operador"],
    condicaoSeguro: String(regraRecord.condicaoSeguro ?? "QUALQUER") as CondicaoSeguroConciliacao,
    origem: {
      fonte: origemFonte,
      campo: String(origemRecord.campo ?? ""),
      rotulo: String(origemRecord.rotulo ?? ""),
    },
    destino: {
      fonte: destinoFonte,
      campo: String(destinoRecord.campo ?? ""),
      rotulo: String(destinoRecord.rotulo ?? ""),
    },
  };
};

const normalizeConfiguracao = (
  configuracao: ConfiguracaoConciliacao
): CreateConfiguracaoConciliacaoPayload => {
  const configuracaoConteudo = parseConfiguracaoConteudo(configuracao.configuracao);
  const regrasRaw = Array.isArray(configuracaoConteudo.regras)
    ? configuracaoConteudo.regras
    : Array.isArray(configuracaoConteudo.rules)
      ? configuracaoConteudo.rules
      : [];

  const regras = regrasRaw.length
    ? regrasRaw.map((regra) => normalizeRegra(regra))
    : [createEmptyRegra()];

  const versao = Number(configuracaoConteudo.versao ?? configuracaoConteudo.version ?? 1);

  return { 
    nome: configuracao.nome,
    ordem: configuracao.ordem,
    condicaoSeguro: configuracao.condicaoSeguro,
    configuracao: {
      versao,
      regras,
    },
  };
};

export default function ConsigConciliationTab({ backgroundStyle }: CommissionsTabProps) {
  const [proposals, setProposals] = useState<ProposalSidebarItem[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [isLoadingProposals, setIsLoadingProposals] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedProposalIds, setSelectedProposalIds] = useState<string[]>([]);
  const [isReconcilingSelected, setIsReconcilingSelected] = useState<boolean>(false);
  

  const [filters, setFilters] = useState<ConsigConciliationFilters>(initialConsigConciliationFilters);
  const [appliedFilters, setAppliedFilters] = useState<ConsigConciliationFilters>(
    initialConsigConciliationFilters
  );
  const [selectedProposalId, setSelectedProposalId] = useState<string | null>(null);
  const [isConfigModalOpen, setIsConfigModalOpen] = useState<boolean>(false);
  const [configuracoes, setConfiguracoes] = useState<ConfiguracaoConciliacao[]>([]);
  const [isLoadingConfiguracoes, setIsLoadingConfiguracoes] = useState<boolean>(false);
  const [isSubmittingConfiguracao, setIsSubmittingConfiguracao] = useState<boolean>(false);
  const [isLoadingConfiguracaoById, setIsLoadingConfiguracaoById] = useState<boolean>(false);
  const [editingConfiguracaoId, setEditingConfiguracaoId] = useState<string | null>(null);
  const [configuracaoForm, setConfiguracaoForm] =
    useState<CreateConfiguracaoConciliacaoPayload>(createInitialConfiguracaoForm());
  const [viewMode, setViewMode] = useState<ViewModeType>("list");
  const toast = useToast();

  const fetchProposals = async (page: number, limit: number) => {
    setIsLoadingProposals(true);
    try {
      const response = viewMode === "list"
        ? await getPropostasFinanceirasListView(page, limit, appliedFilters)
        : await getPropostasFinanceiras(page, limit, appliedFilters);

      const responseData = Array.isArray(response?.data) ? response.data : [];
      const normalizedProposals = responseData
        .map((item: unknown) => normalizeProposalItem(item as Record<string, unknown>))
        .filter((proposal: ProposalSidebarItem) => proposal.id !== "");

      setProposals(normalizedProposals);
      setTotalItems(Number(response?.total ?? normalizedProposals.length));
      setSelectedProposalIds((previous) => {
        const currentIds = new Set(normalizedProposals.map((proposal: ProposalSidebarItem) => proposal.id));
        return previous.filter((proposalId) => currentIds.has(proposalId));
      });

      if (!normalizedProposals.length) {
        setSelectedProposalId(null);
        return;
      }

      const hasSelectedInCurrentList = normalizedProposals.some(
        (proposal: ProposalSidebarItem) => proposal.id === selectedProposalId
      );

      if (!selectedProposalId || !hasSelectedInCurrentList)
        setSelectedProposalId(normalizedProposals[0].id);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Erro desconhecido";

      toast({
        title: "Erro ao carregar propostas: " + errorMessage,
        description: "Ocorreu um erro ao buscar a lista de propostas financeiras.",
        status: "error",
        duration: 5000,
      });
    } finally {
      setIsLoadingProposals(false);
    }
  };

  useEffect(() => {
    void loadConfiguracoes();
  }, []);

  useEffect(() => {
    fetchProposals(1, pageSize);
    setCurrentPage(1);
  }, [appliedFilters, viewMode]);

  const handleApplyFilters = (appliedFilters: ConsigConciliationFilters): void => {
    setAppliedFilters(appliedFilters);
  };

  const handlePaginationChange = (page: number, limit: number) => {
  const totalPages = Math.ceil(totalItems / limit);

  let newPage = page;

  if (limit !== pageSize) {
    newPage = 1;
  }

  if (newPage > totalPages) {
    newPage = totalPages || 1;
  }

  setCurrentPage(newPage);
  setPageSize(limit);

  fetchProposals(newPage, limit);
};

  const loadConfiguracoes = async (): Promise<void> => {
    setIsLoadingConfiguracoes(true);

    try {
      const response = await listConfiguracoesConciliacao();
      setConfiguracoes(response);
    } catch (error) {
      console.error("Erro ao listar configurações de conciliação:", error);
      toast({
        title: "Erro ao listar configurações",
        description: "Não foi possível carregar as configurações de conciliação.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoadingConfiguracoes(false);
    }
  };

  useEffect(() => {
    if (!isConfigModalOpen)
      return;

    void loadConfiguracoes();
  }, [isConfigModalOpen]);

  const validateConfiguracaoForm = (): string | null => {
    if (!configuracaoForm.nome.trim())
      return "O nome da configuração é obrigatório.";

    if (Number.isNaN(configuracaoForm.ordem) || configuracaoForm.ordem <= 0)
      return "A ordem deve ser um número válido maior que zero.";

    if (!configuracaoForm.configuracao.regras.length)
      return "A configuração deve possuir ao menos 1 regra.";

    const hasInvalidRule = configuracaoForm.configuracao.regras.some((regra) => {
      const hasOrigem = Boolean(regra.origem.fonte) && Boolean(regra.origem.campo.trim());
      const hasDestino = Boolean(regra.destino.fonte) && Boolean(regra.destino.campo.trim());
      const hasOperador = Boolean(regra.operador);

      return !hasOrigem || !hasDestino || !hasOperador;
    });

    if (hasInvalidRule)
      return "Cada regra deve ter origem.fonte, origem.campo, destino.fonte, destino.campo e operador.";

    return null;
  };

  const handleOpenConfigModal = (): void => {
    setIsConfigModalOpen(true);
  };

  const handleCloseConfigModal = (): void => {
    setIsConfigModalOpen(false);
  };

  const handleChangeNomeConfiguracao = (value: string): void => {
    setConfiguracaoForm((previous) => ({
      ...previous,
      nome: value,
    }));
  };

  const handleChangeOrdemConfiguracao = (value: number): void => {
    setConfiguracaoForm((previous) => ({
      ...previous,
      ordem: value,
    }));
  };

  // const handleChangeRequireInsuranceConfiguracao = (value: boolean): void => {
  //   setConfiguracaoForm((previous) => ({
  //     ...previous,
  //     apenasComSeguro: value,
  //   }));
  // };

  const handleChangeCondicaoSeguroEtapa = (value: CondicaoSeguroConciliacao) => {
  setConfiguracaoForm((prev) => ({
    ...prev,
    condicaoSeguro: value,
    configuracao: {
      ...prev.configuracao,
    },
  }))
}


  const handleChangeVersaoConfiguracao = (value: number): void => {
    setConfiguracaoForm((previous) => ({
      ...previous,
      configuracao: {
        ...previous.configuracao,
        versao: value,
      },
    }));
  };

  const handleNewConfiguracao = (): void => {
    setEditingConfiguracaoId(null);
    setConfiguracaoForm(createInitialConfiguracaoForm());
  };

  const handleEditConfiguracao = async (id: string): Promise<void> => {
    setIsLoadingConfiguracaoById(true);

    try {
      const configuracao = await getConfiguracaoConciliacaoById(id);
      setEditingConfiguracaoId(configuracao.id);
      setConfiguracaoForm(normalizeConfiguracao(configuracao));

      toast({
        title: "Configuração carregada",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Erro ao buscar configuração por id:", error);
      toast({
        title: "Erro ao carregar configuração",
        description: "Não foi possível carregar os dados da configuração selecionada.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoadingConfiguracaoById(false);
    }
  };

  const handleDeleteConfiguracao = async (id: string): Promise<void> => {
    const shouldDelete = window.confirm(
      "Deseja realmente excluir esta configuração de conciliação?"
    );

    if (!shouldDelete)
      return;

    setIsSubmittingConfiguracao(true);

    try {
      await deleteConfiguracaoConciliacao(id);
      toast({
        title: "Configuração excluída",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      if (editingConfiguracaoId === id)
        handleNewConfiguracao();

      await loadConfiguracoes();
    } catch (error) {
      console.error("Erro ao excluir configuração:", error);
      toast({
        title: "Erro ao excluir configuração",
        description: "Não foi possível excluir a configuração de conciliação.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmittingConfiguracao(false);
    }
  };

  const handleSubmitConfiguracao = async (): Promise<void> => {
    const validationError = validateConfiguracaoForm();

    if (validationError) {
      toast({
        title: "Validação inválida",
        description: validationError,
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsSubmittingConfiguracao(true);

    try {
      if (editingConfiguracaoId) {
        await updateConfiguracaoConciliacao(editingConfiguracaoId, {
          nome: configuracaoForm.nome,
          ordem: configuracaoForm.ordem,
          condicaoSeguro: configuracaoForm.condicaoSeguro,
          configuracao: configuracaoForm.configuracao,
        });

        toast({
          title: "Configuração atualizada",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        await createConfiguracaoConciliacao(configuracaoForm);

        toast({
          title: "Configuração criada",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }

      await loadConfiguracoes();
      handleNewConfiguracao();
    } catch (error) {
      console.error("Erro ao salvar configuração:", error);
      toast({
        title: "Erro ao salvar configuração",
        description: "Não foi possível salvar a configuração de conciliação.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmittingConfiguracao(false);
    }
  };

  const handleAddRegra = (): void => {
    setConfiguracaoForm((previous) => ({
      ...previous,
      configuracao: {
        ...previous.configuracao,
        regras: [...previous.configuracao.regras, createEmptyRegra()],
      },
    }));
  };

  const handleRemoveRegra = (index: number): void => {
    setConfiguracaoForm((previous) => {
      if (previous.configuracao.regras.length === 1)
        return previous;

      return {
        ...previous,
        configuracao: {
          ...previous.configuracao,
          regras: previous.configuracao.regras.filter((_, regraIndex) => regraIndex !== index),
        },
      };
    });
  };

  const handleUpdateRegra = (
    index: number,
    update: Partial<RegraConciliacao>
  ): void => {
    setConfiguracaoForm((previous) => ({
      ...previous,
      configuracao: {
        ...previous.configuracao,
        regras: previous.configuracao.regras.map((regra, regraIndex) => {
          if (regraIndex !== index)
            return regra;

          return {
            ...regra,
            ...update,
          };
        }),
      },
    }));
  };

  const handleUpdateRegraOrigem = (
    index: number,
    update: Partial<RegraConciliacao["origem"]>
  ): void => {
    setConfiguracaoForm((previous) => ({
      ...previous,
      configuracao: {
        ...previous.configuracao,
        regras: previous.configuracao.regras.map((regra, regraIndex) => {
          if (regraIndex !== index)
            return regra;

          return {
            ...regra,
            origem: {
              ...regra.origem,
              ...update,
            },
          };
        }),
      },
    }));
  };

  const handleUpdateRegraDestino = (
    index: number,
    update: Partial<RegraConciliacao["destino"]>
  ): void => {
    setConfiguracaoForm((previous) => ({
      ...previous,
      configuracao: {
        ...previous.configuracao,
        regras: previous.configuracao.regras.map((regra, regraIndex) => {
          if (regraIndex !== index)
            return regra;

          return {
            ...regra,
            destino: {
              ...regra.destino,
              ...update,
            },
          };
        }),
      },
    }));
  };

  const handleToggleProposalSelection = (proposalId: string, checked: boolean): void => {
    setSelectedProposalIds((previous) => {
      if (checked) {
        if (previous.includes(proposalId))
          return previous;

        return [...previous, proposalId];
      }

      return previous.filter((id) => id !== proposalId);
    });
  };

  const handleReconcileSelected = async (): Promise<void> => {
    if (!selectedProposalIds.length) {
      toast({
        title: "Nenhuma proposta selecionada",
        description: "Selecione ao menos uma proposta para reconciliar.",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    if (selectedProposalIds.length > MAX_MASS_RECONCILIATION_ITEMS) {
      toast({
        title: "Limite excedido",
        description: `Selecione no máximo ${MAX_MASS_RECONCILIATION_ITEMS} contratos para conciliar em massa.`,
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsReconcilingSelected(true);

    try {
      const response = await executarConciliacaoEmMassa({
        propostaFinanceiraIds: selectedProposalIds,
      });

      toast({
        title: "Resumo da reconciliação",
        description: `Recebido: ${response.totalRecebido} | Processado: ${response.totalProcessado} | Sucesso: ${response.sucesso.length} | Falhas: ${response.falhas.length}`,
        status:
          !response.sucesso.length && !response.falhas.length
            ? "info"
            : response.falhas.length
              ? "warning"
              : "success",
        duration: 7000,
        isClosable: true,
      });

      setSelectedProposalIds(response.falhas.map((item) => item.propostaFinanceiraId));
      await fetchProposals(currentPage, pageSize);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Erro desconhecido";

      toast({
        title: "Erro ao reconciliar selecionados",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsReconcilingSelected(false);
    }
  };

  const handleConciliationUpdated = async (): Promise<void> => {
    await fetchProposals(currentPage, pageSize);
  };

  const handleChangeModeView = (mode: ViewModeType) => {
    setViewMode(mode)
  }

  return (
    <Box w="100%" minW="0">
      <ConciliationFilters
        backgroundStyle={backgroundStyle}
        filters={filters}
        setFilters={setFilters}
        onApplyFilters={handleApplyFilters}
        handleOpenConfigModal={handleOpenConfigModal}
        handleChangeModeView={handleChangeModeView}
        viewMode={viewMode}
      />

      {viewMode === 'detail' && (
        <ConciliationContentView
          proposals={proposals}
          totalItems={totalItems}
          currentPage={currentPage}
          pageSize={pageSize}
          selectedProposalId={selectedProposalId}
          setSelectedProposalId={setSelectedProposalId}
          selectedProposalIds={selectedProposalIds}
          onToggleProposalSelection={handleToggleProposalSelection}
          onReconcileSelected={handleReconcileSelected}
          isReconcilingSelected={isReconcilingSelected}
          isLoading={isLoadingProposals}
          onPageChange={handlePaginationChange}
          configuracoes={configuracoes}
          onConciliationUpdated={handleConciliationUpdated}
        />
      )}

      {viewMode === 'list' && (
        <ConsignadoListView
          proposals={proposals}
          totalItems={totalItems}
          currentPage={currentPage}
          pageSize={pageSize}  
          onPageChange={handlePaginationChange}
          onConciliationUpdated={handleConciliationUpdated}
          isLoading={isLoadingProposals}
          configuracoes={configuracoes}

        />
      )}

      <ConciliationConfigModal
        isOpen={isConfigModalOpen}
        isLoadingConfiguracoes={isLoadingConfiguracoes}
        isSubmittingConfiguracao={isSubmittingConfiguracao}
        isLoadingConfiguracaoById={isLoadingConfiguracaoById}
        editingConfiguracaoId={editingConfiguracaoId}
        configuracoes={configuracoes}
        configuracaoForm={configuracaoForm}
        onClose={handleCloseConfigModal}
        onNewConfiguracao={handleNewConfiguracao}
        onEditConfiguracao={handleEditConfiguracao}
        onDeleteConfiguracao={handleDeleteConfiguracao}
        onSubmitConfiguracao={handleSubmitConfiguracao}
        onChangeNome={handleChangeNomeConfiguracao}
        onChangeOrdem={handleChangeOrdemConfiguracao}
        onChangeVersao={handleChangeVersaoConfiguracao}
        onChangeCondicaoSeguroEtapa={handleChangeCondicaoSeguroEtapa}
        onAddRegra={handleAddRegra}
        onRemoveRegra={handleRemoveRegra}
        onUpdateRegra={handleUpdateRegra}
        onUpdateRegraOrigem={handleUpdateRegraOrigem}
        onUpdateRegraDestino={handleUpdateRegraDestino}
      />
    </Box>
  );
}
